import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Clock, 
  MapPin, 
  Star, 
  Users, 
  Calendar, 
  ArrowLeft, 
  Binoculars, 
  CircleDollarSign,
  CheckCircle,
  Info,
  Sparkles,
  Heart,
  Building2,
  ExternalLink
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fetchActivityDetails, getActivityPricing } from '@/utils/endUserAppApi';
import { fetchPublicBusinessProfile } from '@/utils/publicBusinessApi';
import { PricingResponse } from '@/types/pricing';
import { ActivityDetail } from '@/types/activitydetail';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { Header } from '@/components/Header';
import { typography, spacing, shadows, colors } from '@/components/business/design-system/businessDesignTokens';

export function ActivityPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('public', { keyPrefix: 'activities' });
  const { t: tc } = useTranslation('common');
  const [activity, setActivity] = useState<ActivityDetail | null>(null);
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [loading, setLoading] = useState(true);

  // Helper function to get the best available location
  const getDisplayLocation = () => {
    if (!activity) return null;
    
    // Try activity location first
    if (activity.location?.address?.name) {
      return {
        name: activity.location.address.name,
        city: activity.location.address.city,
        country: activity.location.address.country,
        coordinates: activity.location.coordinates?.coordinates
      };
    }
    
    // Fall back to activity location city
    if (activity.location?.address?.city) {
      return {
        name: activity.location.address.city,
        city: activity.location.address.city,
        country: activity.location.address.country,
        coordinates: activity.location.coordinates?.coordinates
      };
    }
    
    // Fall back to business location
    if (activity.business?.location) {
      return {
        name: activity.business.location.address?.name || activity.business.location.address?.city,
        city: activity.business.location.address?.city,
        country: activity.business.location.address?.country,
        coordinates: activity.business.location.coordinates?.coordinates
      };
    }
    
    return null;
  };
  
  // Helper function to create Google Maps URL
  const getMapUrl = () => {
    const location = getDisplayLocation();
    if (!location) return null;
    
    if (location.coordinates && location.coordinates.length === 2) {
      // Use coordinates if available
      const [lng, lat] = location.coordinates;
      return `https://www.google.com/maps?q=${lat},${lng}`;
    } else if (location.name && location.city) {
      // Use address search
      const query = encodeURIComponent(`${location.name}, ${location.city}, ${location.country || ''}`);
      return `https://www.google.com/maps/search/?api=1&query=${query}`;
    }
    
    return null;
  };
  
  // Helper function to get display pricing
  const getDisplayPrice = () => {
    if (pricing?.pricing) {
      const adultPrice = pricing.pricing.adults;
      return {
        adult: adultPrice,
        children: pricing.pricing.children,
        students: pricing.pricing.students,
        hasMultiplePrices: (
          adultPrice.amount !== pricing.pricing.children.amount ||
          adultPrice.amount !== pricing.pricing.students.amount
        )
      };
    }
    
    // Fall back to activity.price if no pricing data
    if (activity?.price && !activity.price.isFree) {
      return {
        adult: activity.price,
        children: activity.price,
        students: activity.price,
        hasMultiplePrices: false
      };
    }
    
    return null;
  };
  
  // Mock data for demo purposes
  const getMockAnimals = () => [
    { id: '1', name: 'Norwegian Elk', emoji: '🦌', sightingProbability: 0.85 },
    { id: '2', name: 'Arctic Fox', emoji: '🦊', sightingProbability: 0.65 },
    { id: '3', name: 'Brown Bear', emoji: '🐻', sightingProbability: 0.45 },
    { id: '4', name: 'Golden Eagle', emoji: '🦅', sightingProbability: 0.75 },
    { id: '5', name: 'Lynx', emoji: '🐱', sightingProbability: 0.35 },
    { id: '6', name: 'Reindeer', emoji: '🦌', sightingProbability: 0.90 }
  ];
  
  const getMockDuration = () => ({ value: 3, unit: 'hours' });
  
  const getMockGroupSize = () => 12;
  
  const getMockPricing = () => ({
    adult: { amount: 149.99, currency: 'USD' },
    children: { amount: 99.99, currency: 'USD' },
    students: { amount: 129.99, currency: 'USD' },
    hasMultiplePrices: true
  });
  
  // Helper to get animals with fallback to mock data
  const getDisplayAnimals = () => {
    if (activity?.animals && activity.animals.length > 0) {
      return activity.animals;
    }
    return getMockAnimals();
  };
  
  // Helper to get duration with fallback to mock data
  const getDisplayDuration = () => {
    if (typeof activity?.duration === 'object' && activity.duration?.value) {
      return `${activity.duration.value} ${activity.duration.unit}`;
    } else if (typeof activity?.duration === 'number' && activity.duration > 0) {
      const hours = Math.floor(activity.duration / 60);
      const minutes = activity.duration % 60;
      if (hours > 0 && minutes > 0) {
        return `${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''}`;
      } else {
        return `${minutes} minutes`;
      }
    }
    // Fallback to mock data
    const mock = getMockDuration();
    return `${mock.value} ${mock.unit}`;
  };
  
  // Helper to get group size with fallback to mock data
  const getDisplayGroupSize = () => {
    if (activity?.maxGroupSize && activity.maxGroupSize > 0) {
      return activity.maxGroupSize;
    }
    if (activity?.maxParticipants && activity.maxParticipants > 0) {
      return activity.maxParticipants;
    }
    return getMockGroupSize();
  };
  
  // Enhanced price helper with mock fallback
  const getEnhancedDisplayPrice = () => {
    const realPrice = getDisplayPrice();
    if (realPrice) return realPrice;
    
    // Fallback to mock pricing for demo
    return getMockPricing();
  };

  useEffect(() => {
    const loadActivity = async () => {
      if (!id) {
        setLoading(false);
        return;
      }

      try {
        const activityData = await fetchActivityDetails(id);
        
        // If we have business data, fetch the complete business profile
        if (activityData.business?._id) {
          try {
            const businessData = await fetchPublicBusinessProfile(activityData.business._id);
            // Merge the complete business data while preserving activity-specific business data
            activityData.business = {
              ...businessData,
              hasCustomAdventures: activityData.business.hasCustomAdventures
            };
          } catch (businessError) {
            console.error('Error fetching business details:', businessError);
            // Continue with existing business data if fetch fails
          }
        }
        
        setActivity(activityData);
        
        // Fetch pricing data
        try {
          const pricingData = await getActivityPricing(id);
          setPricing(pricingData);
        } catch (pricingError) {
          console.error('Error fetching pricing:', pricingError);
          // Continue without pricing data
        }
      } catch (error) {
        console.error('Error loading activity:', error);
      } finally {
        setLoading(false);
      }
    };

    loadActivity();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F0] flex items-center justify-center pt-[calc(4rem+env(safe-area-inset-top,0px))]">
        <Header />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#77A146] mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('loading.message')}</p>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-[#F0F4F0] pt-[calc(4rem+env(safe-area-inset-top,0px))]">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
        <h2 className={cn(typography.h2, "text-slate-900")}>{t('notFound.title')}</h2>
        <p className={cn(typography.body, "text-slate-600 mt-2")}>{t('notFound.message')}</p>
        <Button onClick={() => navigate('/')} className="mt-4">
          {tc('actions.backToHome')}
        </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F0F4F0] pt-[calc(4rem+env(safe-area-inset-top,0px))]">
      <Header />

      {/* Activity Header with Cover Image */}
      <div className="relative h-80">
        <div 
          className="w-full h-full bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${activity.images?.[0] || '/images/default-activity.jpg'})`,
            backgroundColor: '#1F2937' 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-black/60"></div>
        
        {/* Navigation Buttons */}
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <Button
            onClick={() => navigate(-1)}
            className="bg-white/20 backdrop-blur-sm hover:bg-white/30"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {tc('actions.back')}
          </Button>
          {activity?.business?._id && (
            <>
              <Button
                onClick={() => navigate(`/business/${activity.business?._id}`)}
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30"
              >
                <Building2 className="w-5 h-5 mr-2" />
                {t('business.viewProfile')}
              </Button>
              {activity.business?.hasCustomAdventures && (
                <Button
                  onClick={() => navigate(`/custom-adventures/${activity.business?._id}`)}
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  {t('business.customAdventures')}
                </Button>
              )}
            </>
          )}
        </div>

        {/* Activity Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
          <div className="container mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200/50 shadow-sm">
                {t('types.' + activity.activityType)}
              </Badge>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200/80 shadow-sm">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                <span className="text-sm font-medium text-blue-700">{activity.rating || '4.5'}</span>
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">{activity.name}</h1>
          </div>
        </div>
      </div>

      {/* Activity Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-8 space-y-8">
            {/* Description Card */}
            <Card className={cn(shadows.level1.className, "border-slate-200/60")}>
              <CardHeader>
                <CardTitle>{t('sections.description')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600">{activity.description}</p>
              </CardContent>
            </Card>

            {/* Quick Info Card */}
            <Card className={cn(shadows.level1.className, "border-slate-200/60")}>
              <CardHeader>
                <CardTitle>{t('sections.details')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-slate-50/80 border border-slate-200/60">
                    <Clock className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="font-medium text-slate-900">{t('details.duration')}</p>
                      <p className="text-sm text-slate-600">
                        {getDisplayDuration()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-slate-50/80 border border-slate-200/60">
                    <MapPin className="w-6 h-6 text-emerald-600" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{t('details.location')}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-slate-600">
                          {(() => {
                            const location = getDisplayLocation();
                            if (!location) return 'Location not specified';
                            return location.city && location.country 
                              ? `${location.name || location.city}, ${location.country}`
                              : location.name || 'Location available';
                          })()}
                        </p>
                        {getMapUrl() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => window.open(getMapUrl()!, '_blank')}
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Map
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-slate-50/80 border border-slate-200/60">
                    <Users className="w-6 h-6 text-purple-600" />
                    <div>
                      <p className="font-medium text-slate-900">{t('details.groupSize')}</p>
                      <p className="text-sm text-slate-600">{getDisplayGroupSize()} {t('details.people')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-slate-50/80 border border-slate-200/60">
                    <CircleDollarSign className="w-6 h-6 text-amber-600" />
                    <div>
                      <p className="font-medium text-slate-900">{t('details.price')}</p>
                      <div className="text-sm text-slate-600">
                        {(() => {
                          const displayPrice = getEnhancedDisplayPrice();
                          
                          if (displayPrice.hasMultiplePrices) {
                            return (
                              <div className="space-y-1">
                                <div>Adult: {new Intl.NumberFormat('en-US', { style: 'currency', currency: displayPrice.adult.currency }).format(displayPrice.adult.amount)}</div>
                                <div>Child: {new Intl.NumberFormat('en-US', { style: 'currency', currency: displayPrice.children.currency }).format(displayPrice.children.amount)}</div>
                                <div>Student: {new Intl.NumberFormat('en-US', { style: 'currency', currency: displayPrice.students.currency }).format(displayPrice.students.amount)}</div>
                              </div>
                            );
                          } else {
                            return `From ${new Intl.NumberFormat('en-US', { style: 'currency', currency: displayPrice.adult.currency }).format(displayPrice.adult.amount)} per person`;
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Wildlife Section */}
            <Card className={cn(shadows.level1.className, "border-slate-200/60")}>
              <CardHeader className="border-b border-slate-100/80">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 border border-emerald-200/50 flex items-center justify-center">
                    <Binoculars className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle>{t('sections.wildlife')}</CardTitle>
                    <CardDescription>{t('wildlife.description')}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {getDisplayAnimals().map((animal, index) => {
                    // Handle both string and object animal types
                    if (typeof animal === 'string') {
                      return (
                        <div 
                          key={`animal-${index}`}
                          className="p-4 rounded-lg bg-slate-50/80 border border-slate-200/60"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-white border border-slate-200/60 flex items-center justify-center text-2xl">
                              🦌
                            </div>
                            <div className="flex-1">
                              <h3 className="font-medium text-slate-900">{animal}</h3>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    
                    // Handle object animal type
                    return (
                      <div 
                        key={animal.id || `animal-${index}`}
                        className="p-4 rounded-lg bg-slate-50/80 border border-slate-200/60 hover:bg-slate-100/80 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg bg-white border border-slate-200/60 flex items-center justify-center text-2xl">
                            {animal.emoji || '🦌'}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-slate-900">{animal.name}</h3>
                            {animal.sightingProbability && (
                              <div className="flex items-center gap-2 mt-2">
                                <Progress value={animal.sightingProbability * 100} className="h-2" />
                                <span className="text-sm text-slate-600 whitespace-nowrap">
                                  {Math.round(animal.sightingProbability * 100)}% {t('wildlife.sightingChance')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Booking Section */}
          <div className="lg:col-span-4">
            <div className="sticky top-[calc(6rem+env(safe-area-inset-top,0px))] space-y-6">
              {/* Booking Card */}
              <Card className={cn(shadows.level2.className, "border-slate-200/60")}>
                <CardHeader className="border-b border-slate-100/80">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 border border-blue-200/50 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-blue-600" />
                    </div>
                    <CardTitle>
                      {(() => {
                        const displayPrice = getEnhancedDisplayPrice();
                        
                        return `From ${new Intl.NumberFormat('en-US', { 
                          style: 'currency', 
                          currency: displayPrice.adult.currency 
                        }).format(displayPrice.adult.amount)}`;
                      })()}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* Pricing Breakdown */}
                  {(() => {
                    const displayPrice = getEnhancedDisplayPrice();
                    if (displayPrice && displayPrice.hasMultiplePrices) {
                      return (
                        <div className="space-y-2 p-3 bg-blue-50/50 rounded-lg border border-blue-100">
                          <h4 className="text-sm font-medium text-slate-900">Pricing per person:</h4>
                          <div className="grid grid-cols-1 gap-1 text-sm">
                            <div className="flex justify-between">
                              <span className="text-slate-600">Adult:</span>
                              <span className="font-medium">{new Intl.NumberFormat('en-US', { style: 'currency', currency: displayPrice.adult.currency }).format(displayPrice.adult.amount)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Child:</span>
                              <span className="font-medium">{new Intl.NumberFormat('en-US', { style: 'currency', currency: displayPrice.children.currency }).format(displayPrice.children.amount)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-slate-600">Student:</span>
                              <span className="font-medium">{new Intl.NumberFormat('en-US', { style: 'currency', currency: displayPrice.students.currency }).format(displayPrice.students.amount)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  {/* Status Pills */}
                  <div className="flex flex-wrap gap-2">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200/80 shadow-sm">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-700">{t('booking.instantBooking')}</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200/80 shadow-sm">
                      <Info className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-700">{t('booking.freeCancellation')}</span>
                    </div>
                  </div>

                  {/* Booking Button */}
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/25 h-12 text-base"
                    onClick={() => navigate(`/booking/${id}`)}
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {t('booking.bookNow')}
                  </Button>

                  {/* Additional Info */}
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>{t('booking.noFees')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>{t('booking.bestPrice')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600" />
                      <span>{t('booking.instantConfirmation')}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Business Card */}
              <Card className={cn(shadows.level1.className, "border-slate-200/60")}>
                <CardHeader>
                  <CardTitle>{t('business.about')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-cover bg-center border-2 border-slate-200" 
                         style={{ backgroundImage: `url(${activity.business?.images?.logo || '/images/default-business.jpg'})` }} />
                    <div>
                      <h3 className="font-medium text-slate-900">
                        {activity.business?.name}
                      </h3>
                      <p className="text-sm text-slate-600">{activity.business?.location?.address?.city}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {activity.business?._id && (
                      <>
                        <Button 
                          variant="outline" 
                          className="w-full" 
                          onClick={() => navigate(`/business/${activity.business?._id}`)}
                        >
                          <Building2 className="w-4 h-4 mr-2" />
                          {t('business.viewProfile')}
                        </Button>
                        {activity.business?.hasCustomAdventures && (
                          <Button 
                            variant="outline" 
                            className="w-full" 
                            onClick={() => navigate(`/custom-adventures/${activity.business?._id}`)}
                          >
                            <Sparkles className="w-4 h-4 mr-2" />
                            {t('business.customAdventures')}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}